type QuarterTotal = {
  quarter: { sortKey: number };
  amount: number;
};

const maximumBarHeight = 168;
const minimumPositiveBarHeight = 14;

export const energyBillUpdatedEvent = "solarcloud:energy-bill-updated";

export function parseStoredEnergyBill(value: string | null): number | undefined {
  const trimmedValue = value?.trim();
  if (
    !trimmedValue ||
    !/^\+?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(trimmedValue)
  ) {
    return undefined;
  }

  const bill = Number(trimmedValue);
  return Number.isFinite(bill) && bill >= 0 ? bill : undefined;
}

export function getChartFractionDigits(amount: number): number {
  if (!Number.isFinite(amount) || Number.isInteger(amount)) return 0;

  const [coefficient, exponentText = "0"] = amount.toString().toLowerCase().split("e");
  const coefficientFractionDigits = coefficient.split(".")[1]?.length ?? 0;
  const precision = coefficientFractionDigits - Number(exponentText);

  return Math.min(100, Math.max(2, precision));
}

export function getChartScaleMax(
  quarters: QuarterTotal[],
  energyBill?: number
): number {
  if (quarters.length === 0) return 0;

  return quarters.reduce(
    (maxAmount, quarter) =>
      Math.max(maxAmount, quarter.amount, energyBill ?? 0),
    0
  );
}

export function findBestQuarterSortKey(
  quarters: QuarterTotal[]
): number | undefined {
  let bestQuarterSortKey: number | undefined;
  let bestAmount = -1;

  for (const quarter of quarters) {
    if (quarter.amount > bestAmount) {
      bestAmount = quarter.amount;
      bestQuarterSortKey = quarter.quarter.sortKey;
    }
  }

  return bestQuarterSortKey;
}

export function getVisibleQuarterRange(
  totalQuarterCount: number,
  visibleQuarterCount: number,
  offsetFromLatest: number
): { start: number; end: number } {
  const end = Math.max(0, totalQuarterCount - offsetFromLatest);

  return {
    start: Math.max(0, end - visibleQuarterCount),
    end,
  };
}

export function getBarHeight(
  amount: number,
  maxTotalAmount: number,
  maxBarHeight = maximumBarHeight
): number {
  if (amount <= 0) return 0;

  const safeMaxTotal =
    Number.isFinite(maxTotalAmount) && maxTotalAmount > 0
      ? maxTotalAmount
      : amount;

  return Math.min(
    maxBarHeight,
    Math.max(minimumPositiveBarHeight, (amount / safeMaxTotal) * maxBarHeight)
  );
}
